<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Business Interest
            </h2>
            <div class="subtitle is-2">
              <div v-html="businessInterestText ? businessInterestText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Business Interest</strong> to report.
            </checkbox-adv>
          </div>
        </div>

        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-business-interest"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-8">
                <textbox-vee
                  id="business-name"
                  v-model="newItem.name"
                  placeholder="Business Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4 is-relative">
                <tooltip
                  v-if="interestHeldTooltip !== ''"
                  :content="interestHeldTooltip"
                  text=""
                  icon-position="after"
                  :absolute-positioned="true"
                  absolute-position="top: -15px; right: 12px;"
                  :tooltip-options="{
                    classes: ['large'],
                  }"
                />
                <textbox-vee
                  id="interest-held"
                  v-model="newItem.interestHeld"
                  type="number"
                  placeholder="Interest Held (xx.xx%)"
                  rules="percentage|required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="business-address"
                  v-model="newItem.streetAddress"
                  placeholder="Business Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="business-city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="business-zip-code"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-biz-int-btn"
                  type="button"
                  class="button is-secondary"
                  @click.prevent="trigerNewItemAction"
                >
                  {{ tableAction }} Business Interest
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
                <div
                  slot="interestHeld"
                  slot-scope="props"
                >
                  {{ props.row.interestHeld }}%
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Business Interest')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import Tooltip from '@/components/phila-ui/Tooltip';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'BusinessInterest',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    Tooltip,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      currentFiling: null,
      items: [],
      isSaving: false,
      newItem: {
        name: '',
        interestHeld: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
      },
      nothingToReport: false,
      table: [
        'name',
        'interestHeld',
        'streetAddress',
        'edit',
        'clone',
        'delete',
      ],
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          name: 'Business Name',
          streetAddress: 'Address',
          interestHeld: 'Interest Held (%)',
        },
      },
    };
  },
  computed: {
    states () {
      return this.controlValueDropdown('state');
    },
    businessInterestText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('businessInterestText');
    },
    businessInterestTooltip () {
      return this.$store.getters[ 'admin/activeControlValues' ]('businessInterestTooltip');
    },
    interestHeldTooltip () {
      let tooltip = [];
      if (this.currentFiling && this.businessInterestTooltip) {
        if (this.currentFiling.stateForm === 'Y') {
          tooltip.push(this.businessInterestTooltip[0].description);
        }
        if (this.currentFiling.cityForm === 'Y') {
          tooltip.push(this.businessInterestTooltip[1].description);
        }
      }
      return tooltip.join('<br>');
    },
  },
  watch: {
    items: {
      deep: true,
      handler: function(items) {
        items.forEach(item => {
          if (item.interestHeld) {
            item.interestHeld = parseFloat(item.interestHeld);
          }
        });
      },
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.businessInterestCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentBusinessInterests () {
      return JSON.parse(JSON.stringify(this.$store.state.businessInterest.businessInterests));
    },
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentBusinessInterests();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'businessInterestText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'businessInterestTooltip'));
      promises.push(this.$store.dispatch('businessInterest/fetchBusinessInterests'));
      await Promise.all(promises);
    },
    saveAndContinue () {
      let promises = [];

      const col = 'businessInterestCompleted';
      const endpoint = 'business-interests';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      //V-model.number won't work here, so we need to parse it ourselves
      this.items.forEach(item => {
        item.interestHeld = parseFloat(item.interestHeld);
      });

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));

      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-business-interest'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
